/**
 * Keyboard semantics for the chat input (T1.11).
 *
 * The input is a true multi-line textarea (`multiline` in MUI). The intended
 * behaviour:
 *   - Enter (no Shift) ......... send the message.
 *   - Shift+Enter .............. insert a newline (native textarea behaviour).
 *   - ArrowUp in an empty input  recall the last sent message.
 *
 * Keeping this decision in a pure function makes the Enter-vs-Shift+Enter
 * behaviour explicit and unit-testable, instead of buried in a component
 * handler where it can silently regress.
 */

/**
 * Decide what a keydown in the chat input should do.
 *
 * @param {{key?: string, shiftKey?: boolean}} e - the keyboard event.
 * @param {{hasText?: boolean, hasMessages?: boolean}} [state]
 *   hasText: the input currently has non-whitespace content.
 *   hasMessages: there is at least one prior message in the conversation.
 * @returns {'send'|'newline'|'recall'|null}
 *   'send'    — caller should preventDefault and submit (if not loading).
 *   'newline' — Shift+Enter; caller does nothing so the textarea inserts a newline.
 *   'recall'  — caller should repopulate the input with the last sent message.
 *   null      — no special handling; let the event proceed natively.
 */
export function chatInputKeyAction(e, { hasText = false, hasMessages = false } = {}) {
  if (!e || !e.key) return null;

  if (e.key === 'Enter') {
    return e.shiftKey ? 'newline' : 'send';
  }

  if (e.key === 'ArrowUp' && !hasText && hasMessages) {
    return 'recall';
  }

  return null;
}
